import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { todayLil, formatLilYearJsx } from '../lil.jsx'

const ROMAN = ['', 'I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII','XIII']

function todayYearMonth() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function gregMonthLabels(locale) {
  const lang = (locale || 'en').startsWith('it') ? 'it' : 'en'
  const fmt = new Intl.DateTimeFormat(lang, { month: 'short' })
  return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2000, i, 1)).toUpperCase())
}

export function MonthPicker({
  open, onClose, mode, viewed, onSelect, yearEra, locale, t, disableFuture = false,
}) {
  const isLil = mode === 'lil'
  const initialYear = isLil ? viewed.year : Number(String(viewed).split('-')[0])
  const [pickerYear, setPickerYear] = useState(initialYear)

  // Re-sync picker year whenever the modal opens (or the viewed month changes)
  useEffect(() => {
    if (open) setPickerYear(initialYear)
  }, [open, initialYear])

  if (!open) return null

  const today         = isLil ? todayLil() : todayYearMonth()
  const todayYear     = isLil ? today.year  : Number(today.split('-')[0])
  const todayMonth    = isLil ? today.month : Number(today.split('-')[1])
  const todaySpecial  = isLil ? !!today.special : false
  const viewedYear    = isLil ? viewed.year  : Number(String(viewed).split('-')[0])
  const viewedMonth   = isLil ? viewed.month : Number(String(viewed).split('-')[1])
  const viewedSpecial = isLil ? !!viewed.special : false

  // Chronological ordinal for future-blocking. The Lil special slot sits at
  // year end (after month XIII), so it maps to 99.
  const ordinal = (y, m, sp) => y * 100 + (sp ? 99 : m)
  const todayOrd = ordinal(todayYear, todayMonth, todaySpecial)
  const nextYearDisabled = disableFuture && pickerYear >= todayYear

  // 'k' is "1".."13" for normal Lil months, "S" for the special slot,
  // or 1..12 for greg.
  const months = isLil
    ? [
        ...Array.from({ length: 13 }, (_, i) => ({ k: i + 1, label: ROMAN[i + 1], special: false })),
        { k: 'S', label: '✦', special: true },
      ]
    : gregMonthLabels(locale).map((label, i) => ({ k: i + 1, label, special: false }))

  function pick(cell) {
    if (isLil) {
      if (cell.special) onSelect({ year: pickerYear, special: true })
      else              onSelect({ year: pickerYear, month: cell.k })
    } else {
      onSelect(`${pickerYear}-${String(cell.k).padStart(2, '0')}`)
    }
    onClose()
  }

  function jumpToToday() {
    onSelect(today)
    onClose()
  }

  const yearLabel = isLil
    ? formatLilYearJsx(pickerYear, yearEra)
    : String(pickerYear)

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal month-picker" onClick={e => e.stopPropagation()}>
        <div className="mp-header">
          <span className="mp-title">{t.transactions.date}</span>
          <button type="button" className="mp-this-month" onClick={jumpToToday}>
            {t.transactions.thisMonth}
          </button>
          <button
            type="button"
            className="mp-close"
            onClick={onClose}
            aria-label={t.common.close}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="mp-year-row">
          <button
            type="button"
            className="mp-year-arrow"
            onClick={() => setPickerYear(y => y - 1)}
            aria-label="Previous year"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <span className="mp-year-label">{yearLabel}</span>
          <button
            type="button"
            className="mp-year-arrow"
            onClick={() => setPickerYear(y => y + 1)}
            disabled={nextYearDisabled}
            aria-label="Next year"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        <div className={`mp-grid ${isLil ? 'mp-grid-lil' : 'mp-grid-greg'}`}>
          {months.map(cell => {
            const selected = pickerYear === viewedYear
              && (cell.special ? viewedSpecial : (!viewedSpecial && cell.k === viewedMonth))
            const isToday = pickerYear === todayYear
              && (cell.special ? todaySpecial : (!todaySpecial && cell.k === todayMonth))
            const isFuture = disableFuture
              && ordinal(pickerYear, Number(cell.k) || 0, cell.special) > todayOrd
            const cls = [
              'mp-month',
              selected     ? 'selected'    : '',
              isToday      ? 'today'       : '',
              cell.special ? 'mp-month-sp' : '',
            ].filter(Boolean).join(' ')
            return (
              <button
                key={cell.k}
                type="button"
                className={cls}
                disabled={isFuture}
                onClick={() => pick(cell)}
              >
                {cell.label}
              </button>
            )
          })}
        </div>
      </div>
    </div>,
    document.body
  )
}
